import React, { useState } from 'react';
import { Alert, Button } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useGetActiveSubscription } from '@/lib/api/hooks/useCreateSubscription';
import PlanSelectionModal from '@/components/profile/PlanSelectionModal';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

export const EXPIRY_WARNING_DAYS = 7;

export const SubscriptionExpiryBanner: React.FC = () => {
  const { data: response } = useGetActiveSubscription();
  const queryClient = useQueryClient();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const subscription = response?.data;
  const access = subscription?.access;
  if (!access) return null;

  const { state, endDate, graceUntil } = access;
  const planName = subscription?.plan?.name || 'your';

  const handlePaymentSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['active-subscription'] });
    setIsModalVisible(false);
  };

  const formatDate = (dateStr: string) => {
    return dayjs(dateStr).format('DD MMM YYYY');
  };

  const renewAction = (
    <Button size="small" type="primary" onClick={() => setIsModalVisible(true)}>
      Renew
    </Button>
  );

  let bannerType: 'info' | 'success' | 'warning' | 'error' | undefined;
  let message: React.ReactNode;
  let closable = false;
  let action: React.ReactNode = renewAction;

  switch (state) {
    case 'ACTIVE': {
      // Days to endDate derived from dates so it agrees with endDate (daysRemaining counts to graceUntil)
      const daysToEnd = dayjs(endDate).diff(dayjs().startOf('day'), 'day');
      if (daysToEnd > EXPIRY_WARNING_DAYS || daysToEnd < 0) return null;
      if (isDismissed) return null;
      bannerType = 'warning';
      const daysText = daysToEnd === 1 ? '1 day left' : `${daysToEnd} days left`;
      message = `Your ${planName} plan expires on ${formatDate(endDate)} (${daysText})`;
      closable = true;
      break;
    }
    case 'GRACE':
      bannerType = 'warning';
      message = `Access will be lost on ${formatDate(graceUntil!)}.`;
      break;
    case 'RESTRICTED':
      bannerType = 'error';
      message = `Your access is limited. Renew your ${planName} plan to restore full access.`;
      break;
    case 'EXPIRED':
      bannerType = 'error';
      message = `Your ${planName} plan expired on ${formatDate(endDate)}.`;
      break;
    case 'SUSPENDED':
      bannerType = 'error';
      message = (
        <span>
          Your account has been suspended. Please{' '}
          <Link to="/suspended" style={{ textDecoration: 'underline', fontWeight: 500 }}>
            view suspension details
          </Link>{' '}
          or contact support.
        </span>
      );
      action = undefined;
      break;
    case 'NOT_APPLICABLE':
    default:
      return null;
  }

  return (
    <>
      <Alert
        banner
        showIcon
        type={bannerType}
        message={message}
        action={action}
        closable={closable}
        onClose={() => setIsDismissed(true)}
      />
      <PlanSelectionModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </>
  );
};
